import React, { useState } from 'react'
import { lmsAdmin, LmsAdminError } from '../api'
import { LmsIcon } from '../lms-icon'

// Manual Linking (docs/08 §15, design edxr-manual-linking-v1.0.html —
// the re-link variant). v1 backs create-only POST /admin/lms/account-links;
// re-linking an already-linked LMS user = archive the existing link, then
// retry — offered inline when the backend answers LINK_ALREADY_EXISTS.
// The artifact's duplicate-user merge wizard has no v1 backend
// (deviations D-046).
//
// The form links SCORM student ids. The API also takes lms_user_id_kind (and
// lms_issuer for an xapi_account), but the form has no control for them: only
// SCORM launches exist today, so an xAPI link made here could not be matched,
// and an xapi_account needs a second field (its home page) the form has no
// place for. Add them with the cmi5 / xAPI launches (phase B).
//
// On an LTI 1.3 connection (Phase L, L-10) the id is the LMS's LTI subject
// (`sub`), in the id space of the registration's issuer: the form says so and
// sends the kind `lti13`, which the service accepts only on an LTI 1.3
// connection that has been registered.

export const ManualLinkModal = ({ connections, onClose, onCreated }) => {
  const [connectionId, updateConnectionId] = useState('')
  const [lmsUserId, updateLmsUserId] = useState('')
  const [edxrUserId, updateEdxrUserId] = useState('')
  const [reason, updateReason] = useState('')
  const [error, updateError] = useState(null)
  const [conflictLinkId, updateConflictLinkId] = useState(null)
  const [submitting, updateSubmitting] = useState(false)

  const valid = connectionId && lmsUserId.trim() && Number(edxrUserId) > 0 && reason.trim()
  const isLti = ((connections || []).find(connection => String(connection.id) === String(connectionId)) || {}).delivery === 'lti13'

  const submit = async () => {
    if (!valid || submitting) return
    updateSubmitting(true)
    updateConflictLinkId(null)
    try {
      await lmsAdmin.createManualLink({
        connection_id: Number(connectionId),
        lms_user_id: lmsUserId.trim(),
        ...(isLti ? { lms_user_id_kind: 'lti13' } : {}),
        edxr_user_id: Number(edxrUserId),
        reason: reason.trim()
      })
      await onCreated()
    } catch (e) {
      updateError(e)
      if (e instanceof LmsAdminError && e.code === 'LINK_ALREADY_EXISTS' && e.details.existing_link_id) {
        updateConflictLinkId(e.details.existing_link_id)
      }
      updateSubmitting(false)
    }
  }

  const archiveAndRetry = async () => {
    updateSubmitting(true)
    try {
      await lmsAdmin.archiveLink(conflictLinkId)
      updateConflictLinkId(null)
      updateError(null)
      updateSubmitting(false)
      await submit()
    } catch (e) {
      updateError(e)
      updateSubmitting(false)
    }
  }

  return (
    <div className="lms-modal-scrim" data-testid="manual-link-modal">
      <div className="lms-modal">
        <div className="modal-head">
          <div className="icon">
            <LmsIcon name="link" />
          </div>
          <div className="body">
            <h2>Manually link an LMS account</h2>
            <p className="target">Admin override for the automatic linking flow</p>
          </div>
          <button className="close-btn" title="Close" type="button" onClick={onClose}>
            <LmsIcon name="xmark" />
          </button>
        </div>

        <div className="lms-modal-body">
          <div className="visibility-notice" style={{ marginTop: 0, marginBottom: 14 }}>
            <strong>Re-linking moves an LMS account from one EducationXR user to another.</strong> Existing sessions
            stay with the original user; future launches from the LMS account route to the new user.
          </div>

          {error && (
            <div className="alert alert-danger" role="alert" data-testid="manual-link-error">
              {error.message}
              {conflictLinkId && (
                <div style={{ marginTop: 6 }}>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    type="button"
                    data-testid="archive-and-retry"
                    disabled={submitting}
                    onClick={archiveAndRetry}
                  >
                    <LmsIcon name="right-left" className="me-1" />
                    Archive existing link #{conflictLinkId} and re-link
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="field">
            <label className="field-label">LMS connection</label>
            <select
              className="form-select form-select-sm"
              value={connectionId}
              data-testid="manual-link-connection"
              onChange={e => updateConnectionId(e.target.value)}
            >
              <option value="">Choose a connection…</option>
              {(connections || []).map(connection => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label">LMS user ID</label>
            <input
              className="form-control form-control-sm"
              type="text"
              placeholder={isLti ? 'The subject (sub) the LMS sends, e.g. 8842' : 'The student id the LMS sends, e.g. u_8842'}
              value={lmsUserId}
              data-testid="manual-link-lms-user"
              onChange={e => updateLmsUserId(e.target.value)}
            />
            <div className="field-help" data-testid="manual-link-lms-user-help">
              {isLti
                ? "The subject (sub) your LMS sends for this person in an LTI 1.3 launch, exactly as it sends it. Moodle's is the user's numeric id. The link applies to the LMS registered on this connection. Once it is linked, that person's launches go to the linked account; if the connection turns on \"Require a sign-in for every launch\", they still sign in as that account first."
                : "Exactly as the LMS provides it in launches, capitals and punctuation included — linking matches on this value within the connection's LMS."}
            </div>
          </div>

          <div className="field">
            <label className="field-label">EducationXR user ID</label>
            <input
              className="form-control form-control-sm"
              type="number"
              min="1"
              placeholder="Numeric user id, e.g. 23349"
              value={edxrUserId}
              data-testid="manual-link-edxr-user"
              onChange={e => updateEdxrUserId(e.target.value)}
            />
            <div className="field-help">Must belong to this organization (find it on the Users page).</div>
          </div>

          <div className="field">
            <label className="field-label">Reason</label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              placeholder="Why this link is being created manually — recorded in the audit trail."
              value={reason}
              data-testid="manual-link-reason"
              onChange={e => updateReason(e.target.value)}
            />
          </div>

          <div className="build-banner danger" style={{ marginBottom: 0 }}>
            <LmsIcon name="triangle-exclamation" />
            <div>
              <strong>What happens:</strong> the link is created immediately, the user joins the connection's managed
              learner group, and the next launch from this LMS account routes to the chosen user.
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <span className="help">
            <LmsIcon name="lock" />
            Logged to the audit trail
          </span>
          <button className="btn btn-light border btn-sm" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            disabled={!valid || submitting}
            data-testid="manual-link-submit"
            onClick={submit}
          >
            <LmsIcon name="link" className="me-1" />
            Create link
          </button>
        </div>
      </div>
    </div>
  )
}
